import React from 'react';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom } from 'jotai';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { HEADER_DEVELOPER } from '@smart-tv/components/header/header-helpers';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import styles from '../developer.module.scss';
import Typography from '@smart-tv/components/typography/typography';
import ListItem from '@smart-tv/components/list';
import { networkLogEnabledAtom, topLevelPageReadyAtom } from '@smart-tv/atoms';
import { closeAllDeviceStreams } from '@smart-tv/utils/stream-flow/close-all-device-streams';
import useFeatureIntros from '@smart-tv/hooks/use-feature-intros';
import CpuBenchmark from './developer-cpu-benchmark';
import NetworkSpeedBenchmark from './developer-network-speed';
import DeveloperBuildInfo, { DEVELOPER_BUILD_INFO_FOCUS_KEY } from './developer-build-info';
import DeveloperActiveDevices from './developer-active-devices';
import DeveloperNpvrTest from './developer-npvr-test';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import { mockFeatureIntros } from '@smart-tv/components/feature-intro/feature-intro.mock';
import { activeFeatureIntrosAtom } from '@smart-tv/authenticated-app';

export const Developer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ref, focusKey, hasFocusedChild } = useFocusable({ trackChildren: true });
  const [networkLogEnabled, setNetworkLogEnabled] = useAtom(networkLogEnabledAtom);
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);
  const { resetSeenFeatureIntros } = useFeatureIntros();
  const [closeAllStreamsStatus, setCloseAllStreamsStatus] = React.useState('Press to run');
  const [closingAllStreams, setClosingAllStreams] = React.useState(false);
  const [resetFeatureIntrosStatus, setResetFeatureIntrosStatus] = React.useState('Press to run');
  const [loadInMockFeatureIntrosStatus, setLoadInMockFeatureIntrosStatus] =
    React.useState('Press to run');

  const setActiveFeatureIntros = useSetAtom(activeFeatureIntrosAtom);

  const scrollingRef = React.useRef<HTMLDivElement>(null);

  const scrolling = React.useCallback(({ node }: { node: HTMLElement }) => {
    if (scrollingRef.current != null) {
      scrollingRef.current.scrollTo({
        top: node.offsetTop - 250,
        behavior: 'auto'
      });
    }
  }, []);

  React.useEffect(() => {
    setTopLevelPageReady(true);
  }, [setTopLevelPageReady]);

  React.useEffect(() => {
    if (location.state?.focusFirst !== true) return;

    setFocus(DEVELOPER_BUILD_INFO_FOCUS_KEY);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const handleBack = React.useCallback(
    (event: KeyboardEvent) => {
      if (IS_BROWSER || !hasFocusedChild || !isBackKey(event)) return;

      event.preventDefault();
      event.stopPropagation();
      setFocus(HEADER_DEVELOPER);
    },
    [hasFocusedChild]
  );

  useEventListener('keydown', handleBack);

  const handleCloseAllStreams = React.useCallback(async () => {
    if (closingAllStreams) return;

    setClosingAllStreams(true);
    setCloseAllStreamsStatus('Closing…');

    const result = await closeAllDeviceStreams();
    setCloseAllStreamsStatus(result.success ? 'Closed all streams' : result.message);
    setClosingAllStreams(false);
  }, [closingAllStreams]);

  const handleResetFeatureIntros = React.useCallback(() => {
    resetSeenFeatureIntros();
    setResetFeatureIntrosStatus('Seen feature intros cleared');
  }, [resetSeenFeatureIntros]);

  const handleLoadInMockFeatureIntros = React.useCallback(() => {
    setActiveFeatureIntros(mockFeatureIntros);
    resetSeenFeatureIntros();
    setLoadInMockFeatureIntrosStatus(
      'Loaded in mock feature intros and cleared seen feature intros'
    );
  }, [setActiveFeatureIntros, resetSeenFeatureIntros]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className={styles.page}>
        <div className={styles['column-left']}>
          <Typography variant="h2" className={styles.title}>
            Developer Page
          </Typography>

          <div className={styles.content}>
            <Typography variant="body">
              This area is intended for testing and development purposes. It is not meant to be a
              final product, but rather a space for developers and QA to experiment, diagnose, and
              test new features.
            </Typography>
            <Typography variant="body">
              Please feel free to explore and provide feedback on improvements or changes that could
              be made.
            </Typography>
          </div>
        </div>
        <div className={styles['column-right']} ref={scrollingRef}>
          <div className={styles.section}>
            <Typography className={styles['section__headline']}>Build</Typography>
            <DeveloperBuildInfo onFocus={scrolling} />
          </div>
          
          <div className={styles.section}>
            <Typography className={styles['section__headline']}>Diagnostics</Typography>
            <CpuBenchmark listItemClassName={styles['section__item']} onFocus={scrolling} />
            <NetworkSpeedBenchmark
              listItemClassName={styles['section__item']}
              onFocus={scrolling}
            />
          </div>
          
          <div className={styles.section}>
            <Typography className={styles['section__headline']}>Active devices</Typography>
            <DeveloperActiveDevices
              listItemClassName={styles['section__item']}
              onFocus={scrolling}
            />
            <ListItem
              listItemProps={{ className: styles['section__item'] }}
              onEnterPress={handleCloseAllStreams}
              onFocus={scrolling}
              data={['Close all streams', closeAllStreamsStatus]}
            />
          </div>

          <div className={styles.section}>
            <Typography className={styles['section__headline']}>Monitoring</Typography>
            <ListItem
              listItemProps={{ className: styles['section__item'] }}
              onEnterPress={() => setNetworkLogEnabled((v) => !v)}
              onFocus={scrolling}
              data={['Network Log', networkLogEnabled ? 'Aktiveret' : 'Deaktiveret']}
            />
          </div>
          
          <div className={styles.section}>
            <Typography className={styles['section__headline']}>Feature intros</Typography>
            <ListItem
              listItemProps={{ className: styles['section__item'] }}
              onEnterPress={handleResetFeatureIntros}
              onFocus={scrolling}
              data={['Reset seen feature intros', resetFeatureIntrosStatus]}
            />
            <ListItem
              listItemProps={{ className: styles['section__item'] }}
              onEnterPress={handleLoadInMockFeatureIntros}
              onFocus={scrolling}
              data={['overwrite feature intros with mock data', loadInMockFeatureIntrosStatus]}
            />
          </div>

          <div className={styles.section}>
            <Typography className={styles['section__headline']}>NPVR notifications</Typography>
            <DeveloperNpvrTest listItemClassName={styles['section__item']} onFocus={scrolling} />
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default Developer;
